// src/pages/ExpensePurchaseOrder/epoErrors.js
//
// Batch 5 / Round 13 — ONE place that turns an axios error into a clear,
// user-facing message, so every EPO screen surfaces backend errors the same
// way. Before this, each screen extracted errors slightly differently and a
// few backend shapes (ASP.NET ProblemDetails `title`, ModelState `errors`)
// could fall through to a generic "Action failed."
//
// It understands the shapes this API actually returns:
//   • { message: "..." }            (our BadRequest(new { ex.Message }))
//   • { Message: "..." }            (PascalCase variant)
//   • { title: "...", errors: {...} } (ASP.NET ProblemDetails / ModelState)
//   • plain string body
// …and maps the common status codes (401/403/404/5xx, network) to friendly
// text. The over-receive / not-approved / required-comment messages the
// receiving service raises come back as { message } and pass straight through.

const firstModelStateError = (errors) => {
    if (!errors || typeof errors !== "object") return null;
    for (const key of Object.keys(errors)) {
        const v = errors[key];
        if (Array.isArray(v) && v.length > 0 && typeof v[0] === "string") return v[0];
        if (typeof v === "string") return v;
    }
    return null;
};

/**
 * Extract a friendly message from an axios error.
 * @param {*} e axios error (or anything)
 * @param {string} fallback message when nothing better can be derived
 * @param {object} statusText optional per-status overrides
 *        e.g. { 403: "You can't receive items on this EPO." }
 */
export function epoErrorMessage(e, fallback = "Something went wrong. Please try again.", statusText = {}) {
    // Network / no-response (server down, CORS, timeout).
    if (e && e.request && !e.response) {
        return "Couldn't reach the server. Check your connection and try again.";
    }

    const status = e?.response?.status;
    const data = e?.response?.data;

    // Body-level message takes priority — these are our intentional, specific
    // messages (over-receive, not-approved, comment-required, etc.).
    if (data) {
        if (typeof data === "string" && data.trim()) return data.trim();
        const msg =
            data.message ??
            data.Message ??
            data.title ??
            data.Title ??
            firstModelStateError(data.errors ?? data.Errors);
        if (msg && String(msg).trim()) return String(msg).trim();
    }

    // Caller-provided status overrides.
    if (status && statusText[status]) return statusText[status];

    // Sensible status defaults.
    switch (status) {
        case 400: return "That request couldn't be processed. Please review the values and try again.";
        case 401: return "Your session has expired. Please sign in again.";
        case 403: return "You don't have permission to do that.";
        case 404: return "That item could not be found.";
        case 409: return "That action conflicts with the current state. Refresh and try again.";
        case 422: return "Some values are invalid. Please review and try again.";
        default:
            if (status && status >= 500) return "The server hit an error. Please try again in a moment.";
            return e?.message && e.message !== "Network Error" ? e.message : fallback;
    }
}

export default epoErrorMessage;
